import React from "react";
import { Link } from "react-router-dom";

export default function TermsOfServicePage() {
    const lastUpdated = "March 16, 2026";

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                    Terms of Service
                </h1>
                <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

                <div className="space-y-8 text-gray-700 leading-relaxed">
                    {/* Agreement */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Agreement to Terms</h2>
                        <p>
                            By accessing or using OmniWorth, you agree to be bound by these Terms of
                            Service. If you do not agree to these terms, you may not use OmniWorth.
                        </p>
                        <p className="mt-3">
                            We may update these terms from time to time. Continued use of OmniWorth
                            after changes constitutes acceptance of the modified terms.
                        </p>
                    </section>

                    {/* Description of Service */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
                        <p>
                            OmniWorth LLC ("OmniWorth", "we", "us") is a personal finance application that aggregates your financial
                            account data to help you track your net worth, investments, and financial goals.
                            The Service connects to your financial institutions through Plaid to retrieve
                            account information.
                        </p>
                        <div className="mt-4 bg-amber-50 border border-amber-200 p-4 rounded-lg">
                            <p className="text-amber-700 font-medium">
                                Important: OmniWorth is an informational tool only. We do not provide
                                financial, investment, tax, or legal advice. The information displayed
                                is for personal tracking purposes and should not be relied upon for
                                making financial decisions.
                            </p>
                        </div>
                    </section>

                    {/* Eligibility */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Eligibility</h2>
                        <p>To use OmniWorth, you must:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>Be at least 18 years of age</li>
                            <li>Have the legal capacity to enter into a binding agreement</li>
                            <li>Be a resident of a jurisdiction where the Service is available</li>
                            <li>Provide accurate and complete registration information</li>
                        </ul>
                    </section>

                    {/* Account Registration */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Account Registration and Security</h2>
                        <p>
                            You are responsible for maintaining the confidentiality of your account
                            credentials and for all activities that occur under your account. You agree to:
                        </p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>Create a strong, unique password</li>
                            <li>Not share your login credentials with others</li>
                            <li>Notify us immediately of any unauthorized access</li>
                            <li>Keep your account information up to date</li>
                        </ul>
                        <p className="mt-3">
                            We are not liable for any loss or damage arising from your failure to
                            protect your account credentials.
                        </p>
                    </section>

                    {/* Subscription and Payments */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Subscription and Payments</h2>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Free Trial</h3>
                        <p>
                            New users may be eligible for a free trial period. At the end of the trial,
                            you will be charged for the subscription unless you cancel before the trial ends.
                        </p>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Billing</h3>
                        <p>
                            Subscriptions are billed in advance on a recurring basis (monthly or annually,
                            depending on your selected plan). Payments are processed through Stripe.
                        </p>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Cancellation</h3>
                        <p>
                            You may cancel your subscription at any time through your account settings.
                            Cancellation takes effect at the end of the current billing period. We do not
                            provide refunds for partial billing periods.
                        </p>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Price Changes</h3>
                        <p>
                            We reserve the right to change subscription prices. We will provide at least
                            30 days notice before any price increase takes effect.
                        </p>
                    </section>

                    {/* Linked Accounts */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Linked Financial Accounts</h2>
                        <p>
                            When you connect financial accounts through Plaid, you authorize OmniWorth to
                            access your account information for the purpose of providing the Service. You
                            acknowledge that:
                        </p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>You have the right to link the accounts you connect</li>
                            <li>Account data may not always be accurate or up-to-date</li>
                            <li>Some institutions may not be supported or may have limited functionality</li>
                            <li>You can disconnect accounts at any time</li>
                        </ul>
                        <p className="mt-3">
                            OmniWorth has read-only access to your accounts. We cannot initiate transactions,
                            transfer funds, or make changes to your accounts.
                        </p>
                    </section>

                    {/* Acceptable Use */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Acceptable Use</h2>
                        <p>You agree not to:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>Use the Service for any illegal purpose</li>
                            <li>Attempt to gain unauthorized access to our systems</li>
                            <li>Interfere with or disrupt the Service</li>
                            <li>Reverse engineer or attempt to extract source code</li>
                            <li>Use automated tools to access the Service without permission</li>
                            <li>Impersonate another person or entity</li>
                            <li>Share your account with others or allow unauthorized access</li>
                        </ul>
                        <p className="mt-3">
                            Violation of these terms may result in immediate termination of your account.
                        </p>
                    </section>

                    {/* Intellectual Property */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Intellectual Property</h2>
                        <p>
                            The Service and its original content, features, and functionality are owned
                            by OmniWorth LLC and are protected by copyright, trademark, and other intellectual
                            property laws. You may not copy, modify, distribute, or create derivative works
                            without our express written permission.
                        </p>
                        <p className="mt-3">
                            Your financial data remains your property. By using the Service, you grant us
                            a limited license to use your data solely for the purpose of providing the Service.
                        </p>
                    </section>

                    {/* Disclaimer of Warranties */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Disclaimer of Warranties</h2>
                        <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                            <p className="text-gray-700">
                                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
                                ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE
                                UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
                            </p>
                            <p className="mt-3 text-gray-700">
                                WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR TIMELINESS OF ANY
                                FINANCIAL DATA DISPLAYED IN THE SERVICE. ACCOUNT BALANCES AND VALUES
                                MAY BE DELAYED OR INACCURATE.
                            </p>
                        </div>
                    </section>

                    {/* Limitation of Liability */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Limitation of Liability</h2>
                        <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                            <p className="text-gray-700">
                                TO THE MAXIMUM EXTENT PERMITTED BY LAW, OMNIWORTH LLC SHALL NOT BE LIABLE FOR
                                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
                                INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES.
                            </p>
                            <p className="mt-3 text-gray-700">
                                OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM YOUR USE OF THE SERVICE
                                SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING
                                THE CLAIM.
                            </p>
                        </div>
                    </section>

                    {/* Indemnification */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Indemnification</h2>
                        <p>
                            You agree to indemnify and hold harmless OmniWorth LLC, its officers, directors,
                            employees, and agents from any claims, damages, losses, or expenses (including
                            reasonable attorney fees) arising from your use of the Service or violation
                            of these Terms.
                        </p>
                    </section>

                    {/* Termination */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Termination</h2>
                        <p>
                            We may terminate or suspend your account immediately, without prior notice,
                            if you breach these Terms or for any other reason at our sole discretion.
                        </p>
                        <p className="mt-3">
                            Upon termination, your right to use the Service ceases immediately. You may
                            request deletion of your data by contacting us.
                        </p>
                    </section>

                    {/* Governing Law */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Governing Law</h2>
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws
                            of the State of Florida, without regard to conflict of law principles. Any disputes
                            shall be resolved in the state and federal courts located in the State of Florida.
                        </p>
                    </section>

                    {/* Severability */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Severability</h2>
                        <p>
                            If any provision of these Terms is found to be unenforceable, the remaining
                            provisions shall continue in full force and effect.
                        </p>
                    </section>

                    {/* Contact */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">15. Contact Us</h2>
                        <p>
                            If you have questions about these Terms, please contact us:
                        </p>
                        <ul className="mt-2 space-y-1 text-gray-500">
                            <li>Email: <a href="mailto:support@omniworth.net" className="text-accent-600 hover:underline">support@omniworth.net</a></li>
                            <li>Contact form: <Link to="/contact" className="text-accent-600 hover:underline">omniworth.net/contact</Link></li>
                        </ul>
                    </section>
                </div>

                {/* Back link */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                    <Link to="/" className="text-accent-600 hover:underline text-sm">
                        &larr; Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
}
